import { useParams, useNavigate } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  PersonAdd24Regular,
  CheckmarkCircle24Regular,
  Clock24Regular,
} from "@fluentui/react-icons";
import { useAuth } from "../auth/TeamsAuthProvider";
import TicketDetail from "../components/TicketDetail";
import ReplyComposer from "../components/ReplyComposer";
import { useTicketDetail } from "../hooks/useTicketDetail";
import StatusBadge from "../components/StatusBadge";
import PriorityBadge from "../components/PriorityBadge";
import { createTicketsApi, type TicketStatus } from "../api/tickets";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  headerInfo: {
    flex: 1,
    minWidth: 0,
  },
  headerMeta: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
    flexWrap: "wrap",
  },
  headerActions: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  conversation: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalL,
  },
  composer: {
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  notFound: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
});

export default function TicketPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const styles = useStyles();
  const { token, workspaceId, agent } = useAuth();
  const { ticket, comments, loading, refresh } = useTicketDetail(token, workspaceId, id!);

  async function setStatus(status: TicketStatus) {
    if (!ticket) return;
    const api = createTicketsApi(token, workspaceId);
    await api.updateStatus(ticket.id, status);
    refresh();
  }

  async function assignToMe() {
    if (!ticket) return;
    const api = createTicketsApi(token, workspaceId);
    await api.assign(ticket.id, agent.id);
    refresh();
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spinner size="large" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className={styles.notFound}>
        <Text>Ticket not found.</Text>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          onClick={() => navigate(-1)}
        />
        <div className={styles.headerInfo}>
          <Text size={300} weight="semibold" truncate block>
            {ticket.subject}
          </Text>
          <div className={styles.headerMeta}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>#{ticket.ticketNumber}</Text>
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
          </div>
        </div>
        <div className={styles.headerActions}>
          {ticket.assignedToId !== agent.id && (
            <Button
              appearance="subtle"
              icon={<PersonAdd24Regular />}
              title="Assign to me"
              onClick={assignToMe}
            />
          )}
          <Menu>
            <MenuTrigger>
              <Button appearance="subtle" icon={<Clock24Regular />} title="Set status" />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<CheckmarkCircle24Regular />} onClick={() => setStatus("Open")}>Open</MenuItem>
                <MenuItem onClick={() => setStatus("Pending")}>Pending</MenuItem>
                <MenuItem onClick={() => setStatus("Resolved")}>Resolved</MenuItem>
                <MenuItem onClick={() => setStatus("Closed")}>Closed</MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </div>

      <div className={styles.conversation}>
        <TicketDetail ticket={ticket} comments={comments} />
      </div>

      <div className={styles.composer}>
        <ReplyComposer
          ticketId={ticket.id}
          ticketNumber={ticket.ticketNumber}
          teamId={ticket.teamId}
          agent={agent}
          token={token}
          workspaceId={workspaceId}
          onSent={refresh}
        />
      </div>
    </div>
  );
}
